import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { map, tap } from 'rxjs/operators';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { PublicStakeholderDTO } from './types/PublicStakeholderDTO';
import { MinProjectDTO } from './types/MinProjectDTO';
import { FormLayoutService } from 'src/app/shared/components/form-layout/form-layout.service';
import { SubscriptionEmailRequestDTO } from './types/SubscriptionEmailRequestDTO';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  options: any;
  isLoadingMatchingStakeholders$ = signal<boolean>(false);
  projectInfo$: BehaviorSubject<MinProjectDTO | null> = new BehaviorSubject<MinProjectDTO | null>(
    null
  );
  isLoadingEditStakeholder$ = signal<boolean>(false);
  projectGuid: string | null = null;
  stakeGuid: string | null = null;

  constructor(
    private http: HttpClient,
    private urlParamsService: UrlParamsService,
    private formLayoutService: FormLayoutService
  ) {
    this.http = http;
    this.projectGuid = this.urlParamsService.projectGuid;
    this.stakeGuid = this.urlParamsService.stakeGuid;
  }

  getProjectInfo(projectGuid: string): Observable<MinProjectDTO> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };
    return this.http
      .get<MinProjectDTO>(`${environment.ApiUrl}/Project/GetProject/${projectGuid}`, options)
      .pipe(
        tap({
          next: (response: MinProjectDTO) => {
            this.projectInfo$.next(response);
            const currentProjectInfo = this.projectInfo$.getValue();
            if (currentProjectInfo && currentProjectInfo.Settings) {
              this.formLayoutService.setFormLayoutSettings(currentProjectInfo.Settings);
            }
          },
          error: (error) => {
            console.error(error);
          },
        })
      );
  }

  getMatchingStakeholders(
    lastName: string,
    zipcode: string,
    email: string
  ): Observable<PublicStakeholderDTO | null> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };

    this.isLoadingMatchingStakeholders$.set(true);
    return this.http
      .get<PublicStakeholderDTO>(
        `${
          environment.ApiUrl
        }/Stakeholder/FindStakeholder/${lastName}/${zipcode}/${encodeURIComponent(email)}`,
        { ...options, observe: 'response' }
      )
      .pipe(
        map((response) => (response.status === 204 ? null : response.body)),
        tap({
          next: (response) => {
            if (response?.StakeGuid) {
              this.urlParamsService.setStakeGuid(response.StakeGuid);
            } else if (response?.StakeGuid === null) {
              this.urlParamsService.setStakeGuid('');
            }
            this.isLoadingMatchingStakeholders$.set(false);
          },
          error: (error) => {
            console.error(error);
            this.isLoadingMatchingStakeholders$.set(false);
          },
        })
      );
  }

  getAnonymousStakeholder(): Observable<{ StakeGuid: string }> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };

    return this.http
      .get<{ StakeGuid: string }>(`${environment.ApiUrl}/Stakeholder/GetAnonStakeholder`, options)
      .pipe(
        tap({
          error: (error) => {
            console.error(error);
          },
        })
      );
  }

  getStakeholderByProjectAndStakeGuid(
    stakeGuid: string,
    projectGuid: string
  ): Observable<PublicStakeholderDTO> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };
    this.isLoadingEditStakeholder$.set(true);
    return this.http
      .get<PublicStakeholderDTO>(
        `${environment.ApiUrl}/Stakeholder/GetProjectStakeholder/${stakeGuid}/${projectGuid}`,
        options
      )
      .pipe(
        tap({
          next: (response) => {
            this.isLoadingEditStakeholder$.set(false);
          },
          error: (error) => {
            console.error(error);
            this.isLoadingEditStakeholder$.set(false);
          },
        })
      );
  }

  createStakeholder(projectGuid: string, stakeholder: PublicStakeholderDTO): Observable<any> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };

    if (stakeholder.IsReceiveEmailUpdate === undefined) {
      stakeholder.IsReceiveEmailUpdate = false;
    }

    this.isLoadingEditStakeholder$.set(true); // Consider different name
    return this.http
      .post<any>(
        `${environment.ApiUrl}/Stakeholder/CreateStakeholder/${projectGuid}`,
        stakeholder,
        options
      )
      .pipe(
        tap({
          next: (response) => {
            this.isLoadingEditStakeholder$.set(false);
          },
          error: (error) => {
            console.error(error);
            this.isLoadingEditStakeholder$.set(false);
          },
        })
      );
  }

  updateStakeholder(stakeGuid: string, stakeholder: PublicStakeholderDTO): Observable<any> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };
    this.isLoadingEditStakeholder$.set(true); // Consider different name
    return this.http
      .put<any>(
        `${environment.ApiUrl}/Stakeholder/UpdateStakeholder/${stakeGuid}`,
        stakeholder,
        options
      )
      .pipe(
        tap({
          next: (response) => {
            this.isLoadingEditStakeholder$.set(false);
          },
          error: (error) => {
            console.error(error);
            this.isLoadingEditStakeholder$.set(false);
          },
        })
      );
  }

  triggerConfirmationSubscriptionEmail(
    projectGuid: string,
    stakeGuid: string,
    isSubscribed: boolean = false
  ): Observable<void> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };
    const payload: SubscriptionEmailRequestDTO = {
      ProjectGuid: projectGuid,
      StakeGuid: stakeGuid,
      Subscribed: isSubscribed === null || isSubscribed === undefined ? true : isSubscribed,
    };

    return this.http.post<void>(`${environment.ApiUrl}/Subscription`, payload, options).pipe(
      tap({
        error: (error) => {
          console.error('Error sending confirmation subscription email:', error);
        },
      })
    );
  }
}
